import { useState, useEffect, useCallback, useRef } from 'react';
import { formResMessage } from 'utils/commonFunc';
import {
  fetchBusesApi,
  fetchDriversApi,
  fetchDriverAssignments,
  fetchSchoolBatchStudentTransportApi,
} from 'actions/admin/transport';
import { fetchAllTransportGroupsPlainService, fetchTransportAreasPlainService } from 'actions/admin/transportFee';

export const indexBy = (list, key) => list.reduce((acc, item) => ({ ...acc, [item[key]]: item }), {});

const groupBy = (list, key) =>
  list.reduce((acc, item) => ({ ...acc, [item[key]]: [...(acc[item[key]] || []), item] }), {});

export const emptyTransportData = {
  buses: [],
  drivers: [],
  routes: [],
  areasByRoute: {},
  studentsByRoute: {},
  takenStudentIds: [],
  assignments: [],
};

// Everything the Transport page and the Transport reports show about buses, drivers, routes (with their stops,
// students and bus/driver assignment), loaded once. Only the very first load reports isLoading, so a later
// reload() refreshes in place (an open modal isn't torn down by its own save). Routes, their students and the
// driver assignments are per batch.
export const useTransportData = ({ schoolId, batchId, token, onError }) => {
  const [data, setData] = useState(emptyTransportData);
  const [isLoading, setIsLoading] = useState(true);
  const hasLoaded = useRef(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const loadAll = useCallback(async () => {
    if (!hasLoaded.current) setIsLoading(true);
    try {
      const [busRes, drivers] = await Promise.all([
        fetchBusesApi({ schoolId, token }),
        fetchDriversApi({ schoolId, token }),
      ]);
      const next = { ...emptyTransportData, buses: busRes.data || [], drivers };
      if (batchId) {
        const [routes, studentTransportRes] = await Promise.all([
          fetchAllTransportGroupsPlainService(schoolId, token),
          fetchSchoolBatchStudentTransportApi({ schoolId, batchId, token }),
        ]);
        next.routes = routes || [];
        const areaLists = await Promise.all(
          next.routes.map(route => fetchTransportAreasPlainService(route.transportFeeStructId, token))
        );
        next.areasByRoute = next.routes.reduce(
          (acc, route, index) => ({ ...acc, [route.transportFeeStructId]: areaLists[index] || [] }),
          {}
        );
        const studentTransports = studentTransportRes.data || [];
        next.studentsByRoute = groupBy(studentTransports, 'transportFeeStructId');
        next.takenStudentIds = studentTransports.map(st => st.studentId);
        next.assignments = await fetchDriverAssignments({ drivers, schoolId, batchId, token });
      }
      setData(next);
    } catch (err) {
      onErrorRef.current?.(formResMessage(err));
    }
    hasLoaded.current = true;
    setIsLoading(false);
  }, [schoolId, batchId, token]);

  useEffect(() => {
    if (schoolId) loadAll();
  }, [schoolId, loadAll]);

  return { data, isLoading, reload: loadAll };
};
